import React, { forwardRef } from "react";
import Button from "./Button";
import { cn } from "../../../lib/utils";

/**
 * PillButton Molecule - Rounded pill-shaped button
 * Used for tags, categories, filters, personality traits, etc.
 *
 * @component
 * @example
 * <PillButton selected={true} onClick={handleClick}>#Stylish</PillButton>
 */
const PillButton = forwardRef(
  (
    {
      children,
      selected = false,
      variant = "default",
      size = "md",
      className = "",
      disabled = false,
      ...props
    },
    ref
  ) => {
    // Map PillButton variants/states to Button classes
    const getStyles = () => {
      switch (variant) {
        case "default":
          return selected
            ? "bg-red-400 text-white border-red-400 hover:bg-red-400"
            : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 hover:border-gray-300";
        case "outline":
          return selected
            ? "bg-red-400 text-white border-red-400"
            : "bg-grey-500 text-red-300 border-2 border-red-200 hover:bg-red-300 hover:text-white hover:border-red-300 ";
        case "solid":
          return selected
            ? "bg-red-500 text-white border-red-500"
            : "bg-gray-200 text-gray-700 border-gray-200 hover:bg-gray-300";
        case "category":
          return selected
            ? "bg-red-400 bg-opacity-100 text-white border-red-400"
            : "bg-red-900 bg-opacity-40 text-white border-red-400 hover:bg-grey-500 hover:text-red-500 hover:border-red-500";
        case "customCategory":
          return "border-red-400 hover:bg-grey-500 hover:text-red-500 hover:border-red-500 border-1 border-red-400";
        case "special":
          return "bg-white/20 text-white border-white/40 hover:bg-red-300";
        default:
          return "";
      }
    };

    return (
      <Button
        ref={ref}
        variant={["custom", "customCategory"].includes(variant) ? "custom" : "ghost"} // Use custom for customCategory to allow full override
        size={size}
        rounded="full"
        className={cn("border", getStyles(), className)}
        disabled={disabled}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

PillButton.displayName = "PillButton";

export default PillButton;
